<template>
  <div
    :aria-label="$t('issues.edit_location')"
    :class="[{
      'issue__map-preview--editable': canEdit,
      'issue__map-preview--error': error,
    }, joinClasses('issue__map-preview', props.modifiers)]"
    @click="edit"
  >
    <div class="issue__map-preview-view">
      <LeafletMap
        v-if="leaflet && modelValue"
        ref="map"
        :diagram="diagram"
        :dragging="false"
        :zooming="false"
        @init="onMapInit(modelValue)"
      />

      <MapPreview
        v-else
        :diagram="diagram"
        :emptyText="emptyText"
        :point="modelValue"
      />

      <div
        v-if="modelValue"
        class="issue__map-overlay"
      >
        <div :class="`issue-point issue-point--${riskRatingLevel}-risk`" />
      </div>
    </div>
    <div
      v-if="operationBounds"
      class="issue__map-preview-info typo-tiny"
    >
      <span class="issue__map-preview-info-coord">
        {{ $t('geo.latitude') }}
        <span :class="{'issue__map-preview-coord': true, 'issue__map-preview-coord--empty': !modelValue }">
          {{ latitude ? latitude : '-' }}
        </span>
      </span>
      <span class="issue__map-preview-info-coord">
        {{ $t('geo.longitude') }}
        <span :class="{'issue__map-preview-coord': true, 'issue__map-preview-coord--empty': !modelValue }">
          {{ longitude ? longitude : '-' }}
        </span>
      </span>
    </div>
  </div>

  <IssueLocationSelectModal
    v-if="modalIsOpen"
    :diagram="diagram"
    :location="modelValue"
    :operationBounds="operationBounds"
    :regions="regions"
    @cancel="closeModal"
    @submit="updateLocation"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import I18n from 'i18n-js';
import joinClasses from '@/functions/join_classes';

import MapPreview from '@/components/generic/map_preview.vue';
import IssueLocationSelectModal from '@/components/issues/issue_location_select_modal.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';

import {
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { screenAtLeast } from '@/functions/screen_width';
import { DotIcon } from '@/vendor/leaflet_mods';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});

const props = defineProps({
  canEdit: { type: Boolean, default: true },
  operationBounds: { type: String, required: false, default: null },
  diagram: { type: String, required: false, default: null },
  error: Boolean,
  modelValue: { type: String, default: null },
  regions: { type: Array, required: true },
  riskRatingLevel: { type: String, default: null },
  open: { type: Boolean, default: false },
  modifiers: { type: Array, default: () => [] },
  leaflet: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'cancel']);

const modalIsOpen = ref(false);
const map = ref(null);
let marker = null;

const latLng = computed(() => {
  if (!props.modelValue) return null;
  return rgeoToLatLng(props.modelValue);
});

const latitude = computed(() => {
  if (!latLng.value) return null;
  return latLng.value[0];
});

const longitude = computed(() => {
  if (!latLng.value) return null;
  return latLng.value[1];
});

const emptyText = computed(() => {
  if (props.canEdit) {
    return I18n.t('issues.no_location');
  } else {
    return I18n.t('issues.unspecified');
  }
});

watch(() => props.open, (value) => {
  value !== null && (modalIsOpen.value = value);
});

function edit() {
  if (!props.canEdit) return;

  modalIsOpen.value = true;
}

function updateLocation(newLocation) {
  emit('update:modelValue', newLocation);
  modalIsOpen.value = false;

  if (map.value && newLocation) {
    let locationData = rgeoToLatLng(newLocation);
    setMarker(...locationData);
    map.value.goTo(locationData, 18);
  }
}

function closeModal() {
  modalIsOpen.value = false;
  emit('cancel', true);
}

async function onMapInit(locationData) {
  if (props.regions) {
    props.regions.forEach(loadRegion);
  }

  if (locationData) {
    let location = rgeoToLatLng(locationData);
    setMarker(...location);
    map.value.goTo(location, 18);
  } else if (!screenAtLeast('medium')) {
    let { lat, lng } = map.value.getCenter();
    setMarker(lat, lng);
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value?.addPolygon(
    latLngs,
    regionPolygonOptions(region.region_type)
  );
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function setMarker(lat, lng) {
  map.value.enableCrosshair(lat, lng);
  if (!marker) {
    marker = map.value.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
}
</script>
